/**
 * Fabric Details Controller
 * Handles fabric item selection and panel switching
 */
export class FabricDetailsController extends HTMLElement {
  connectedCallback() {
    setTimeout(() => {
      this.init();
    }, 0);
  }

  init() {
    this.navItems = this.querySelectorAll(".fabric-details__nav-item");
    this.panels = this.querySelectorAll(".fabric-details__panel");

    this.navItems.forEach((item) => {
      item.addEventListener("click", () => {
        const index = item.dataset.fabricIndex;
        this.selectFabric(index);
      });
    });

    // Theme editor integration
    document.addEventListener("shopify:block:select", (event) => {
      const panel = event.target.closest(".fabric-details__panel");
      if (panel && this.contains(panel)) {
        this.selectFabric(panel.dataset.fabricPanel);
      }
    });
  }

  selectFabric(index) {
    // Update nav items
    this.navItems.forEach((item) => {
      if (item.dataset.fabricIndex === index) {
        item.classList.add("is-active");
      } else {
        item.classList.remove("is-active");
      }
    });

    // Update panels
    this.panels.forEach((panel) => {
      if (panel.dataset.fabricPanel === index) {
        panel.classList.add("is-active");
      } else {
        panel.classList.remove("is-active");
      }
    });

    // Open the first accordion when switching tabs (desktop behavior).
    // Only one accordion is open at a time.
    this.openFirstAccordion();
  }

  openFirstAccordion() {
    const activePanel = this.querySelector(".fabric-details__panel.is-active");
    if (!activePanel) return;

    activePanel.querySelectorAll("fabric-accordion").forEach((group) => {
      const accordions = [...group.querySelectorAll("accordion-custom")].filter(
        (accordion) => accordion.closest("fabric-accordion") === group
      );
      accordions.forEach((accordion, index) => {
        accordion.toggleAttribute("open", index === 0);
      });
    });
  }
}

if (!customElements.get("fabric-details-controller")) {
  customElements.define("fabric-details-controller", FabricDetailsController);
}

/**
 * Fabric Accordion
 * Handles accordion behavior within fabric panels
 * Uses event delegation for reliable click handling
 */
export class FabricAccordion extends HTMLElement {
  constructor() {
    super();
    // Bound once, so the same function is added and removed (no double toggles if the element is moved)
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    // Use event delegation - attach click listener to this element
    this.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
  }

  handleClick(e) {
    // Find if click was on a header
    const header = e.target.closest(".details__header");
    if (!header) return;

    // Find the accordion-custom that contains this header
    const accordion = header.closest("accordion-custom");
    if (!accordion) return;

    // Make sure this accordion is inside this fabric-accordion (not nested in another)
    // Check that this fabric-accordion is the closest fabric-accordion ancestor
    if (accordion.closest("fabric-accordion") !== this) return;

    e.stopPropagation();

    // Only one accordion open at a time: opening one closes the others
    if (accordion.hasAttribute("open")) {
      accordion.removeAttribute("open");
    } else {
      this.closeOthers(accordion);
      accordion.setAttribute("open", "");
    }
  }

  /**
   * Closes every accordion in this group except the given one.
   * @param {HTMLElement} openAccordion
   */
  closeOthers(openAccordion) {
    this.querySelectorAll("accordion-custom").forEach((accordion) => {
      if (accordion === openAccordion) return;
      if (accordion.closest("fabric-accordion") !== this) return;
      accordion.removeAttribute("open");
    });
  }
}

if (!customElements.get("fabric-accordion")) {
  customElements.define("fabric-accordion", FabricAccordion);
}

/**
 * Fabric Accordion Mobile
 * Handles outer accordion behavior for mobile layout
 * Uses event delegation for reliable click handling
 */
export class FabricAccordionMobile extends HTMLElement {
  constructor() {
    super();
    this.handleClick = this.handleClick.bind(this);
  }

  connectedCallback() {
    this.addEventListener("click", this.handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener("click", this.handleClick);
  }

  handleClick(e) {
    // Find if click was on a header
    const header = e.target.closest(".details__header");
    if (!header) return;

    // Find the accordion-custom that contains this header
    const accordion = header.closest("accordion-custom");
    if (!accordion) return;

    // Only handle outer accordions (direct children with mobile-item class)
    // Inner accordions are handled by FabricAccordion
    if (!accordion.classList.contains("fabric-details__mobile-item")) return;
    if (accordion.closest("fabric-accordion-mobile") !== this) return;

    e.stopPropagation();

    const isOpen = accordion.hasAttribute("open");

    if (isOpen) {
      accordion.removeAttribute("open");
    } else {
      // Close other outer accordions
      this.closeOthers(accordion);
      accordion.setAttribute("open", "");
      
      // Reset inner accordions when opening
      this.resetInnerAccordions(accordion);
    }
  }

  closeOthers(openAccordion) {
    const siblings = this.querySelectorAll(".fabric-details__mobile-item");
    siblings.forEach((accordion) => {
      if (accordion !== openAccordion) {
        accordion.removeAttribute("open");
      }
    });
  }

  resetInnerAccordions(outerAccordion) {
    const innerAccordions = outerAccordion.querySelectorAll(
      "fabric-accordion accordion-custom"
    );
    innerAccordions.forEach((accordion) => {
      accordion.removeAttribute("open");
    });
  }
}

if (!customElements.get("fabric-accordion-mobile")) {
  customElements.define("fabric-accordion-mobile", FabricAccordionMobile);
}
